import { reactive } from "vue";
import telegram from "@/services/telegram";

/**
 * Глобальные тосты. Отрисовывает <ToastHost/> (смонтирован в App.vue).
 *
 *   import { useToast } from '@/composables/useToast'
 *   const toast = useToast()
 *   toast.show('Турниры скоро')                         // info, 3 c
 *   toast.show('Имя сохранено', { type: 'success' })
 *   toast.show('Ошибка сети', { type: 'error', timeout: 5000 })
 *   toast.success('…'); toast.error('…'); toast.info('…')
 *   const id = toast.show('…', { timeout: 0 }); toast.hide(id)   // timeout 0 — пока не закроют
 *
 * Вне компонентов можно импортировать `toast` напрямую: import { toast } from '@/composables/useToast'.
 */

const MAX_TOASTS = 3;
const DEFAULT_TIMEOUT = 3000;

const state = reactive({ items: [] });
let seq = 0;
const timers = new Map();

function hide(id) {
  const index = state.items.findIndex((t) => t.id === id);
  if (index !== -1) state.items.splice(index, 1);
  clearTimeout(timers.get(id));
  timers.delete(id);
}

/**
 * Показать тост.
 * @param {string} text
 * @param {{type?: 'info'|'success'|'error', timeout?: number}} [options]
 * @returns {number} id тоста
 */
function show(text, options = {}) {
  const type = ["info", "success", "error"].includes(options.type) ? options.type : "info";
  const timeout = options.timeout === undefined ? (type === "error" ? 4500 : DEFAULT_TIMEOUT) : options.timeout;
  const message = String(text || "");
  const duplicate = state.items.find((t) => t.text === message && t.type === type);
  if (duplicate) hide(duplicate.id);
  seq += 1;
  const id = seq;
  state.items.push({ id, text: message, type });
  while (state.items.length > MAX_TOASTS) hide(state.items[0].id);
  if (timeout > 0) timers.set(id, setTimeout(() => hide(id), timeout));
  if (type === "error") telegram.haptic("error");
  else if (type === "success") telegram.haptic("success");
  return id;
}

/** Убрать все тосты. */
function clear() {
  [...state.items].forEach((t) => hide(t.id));
}

export const toast = {
  state,
  show,
  hide,
  clear,
  info: (text, options = {}) => show(text, { ...options, type: "info" }),
  success: (text, options = {}) => show(text, { ...options, type: "success" }),
  error: (text, options = {}) => show(text, { ...options, type: "error" }),
};

/** Доступ к глобальным тостам из компонентов. */
export function useToast() {
  return toast;
}

export default useToast;
